"use strict";
// (C) 2019-2022 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableHeadline = void 0;
var tslib_1 = require("tslib");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_charts_1 = require("@gooddata/sdk-ui-charts");
var react_1 = tslib_1.__importDefault(require("react"));
var bucket_1 = require("../../../constants/bucket");
var Visualization_1 = require("../../../interfaces/Visualization");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var bucketRules_1 = require("../../../utils/bucketRules");
var domHelper_1 = require("../../../utils/domHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var headlineUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/headlineUiConfigHelper");
var UnsupportedConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/UnsupportedConfigurationPanel"));
var AbstractPluggableVisualization_1 = require("../AbstractPluggableVisualization");
var headlineBucketHelper_1 = require("./headlineBucketHelper");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
/**
 * PluggableHeadline
 *
 * ## Buckets
 *
 * | Name             | Id                 | Accepts       |
 * |------------------|--------------------|---------------|
 * | MeasurePrimary   | measures           | measures only |
 * | MeasureSecondary | secondary_measures | measures only |
 *
 * ### Bucket axioms
 *
 * - |MeasurePrimary| = 1
 * - |MeasureSecondary| ≤ 1
 *
 * ## Dimensions
 *
 * The PluggableHeadline always creates one dimensional execution.
 *
 * - ⊤ ⇒ [[MeasureGroupIdentifier]]
 *
 * ## Sorts
 *
 * The PluggableHeadline does not use any sorts.
 */
var PluggableHeadline = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableHeadline, _super);
    function PluggableHeadline(props) {
        var _this = _super.call(this, props) || this;
        _this.settings = props.featureFlags;
        _this.renderFun = props.renderFun;
        return _this;
    }
    PluggableHeadline.prototype.unmount = function () {
        domHelper_1.unmountComponentsAtNodes([this.getElement(), this.getConfigPanelElement()]);
    };
    PluggableHeadline.prototype.getExtendedReferencePoint = function (referencePoint) {
        var _a;
        var referencePointCloned = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, referencePointCloned), { uiConfig: headlineUiConfigHelper_1.getDefaultHeadlineUiConfig() });
        if (!bucketRules_1.hasGlobalDateFilter(referencePoint.filters)) {
            newReferencePoint = bucketHelper_1.removeAllArithmeticMeasuresFromDerived(newReferencePoint);
            newReferencePoint = bucketHelper_1.removeAllDerivedMeasures(newReferencePoint);
        }
        var mappedReferencePoint = headlineBucketHelper_1.tryToMapForeignBuckets(newReferencePoint);
        if (mappedReferencePoint) {
            newReferencePoint = mappedReferencePoint;
        }
        else {
            var limitedBuckets = bucketHelper_1.limitNumberOfMeasuresInBuckets(newReferencePoint.buckets, 2, true);
            var allMeasures = bucketHelper_1.getAllItemsByType(limitedBuckets, [bucket_1.METRIC]);
            var primaryMeasure = allMeasures.length > 0 ? allMeasures[0] : null;
            var secondaryMeasure = headlineBucketHelper_1.findComplementaryOverTimeComparisonMeasure(primaryMeasure, allMeasures) ||
                headlineBucketHelper_1.findSecondMasterMeasure(allMeasures);
            newReferencePoint = headlineBucketHelper_1.setHeadlineRefPointBuckets(newReferencePoint, primaryMeasure, secondaryMeasure);
        }
        bucketConfig_1.configurePercent(newReferencePoint, true);
        bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!((_a = this.settings) === null || _a === void 0 ? void 0 : _a["enableWeekFilters"]));
        newReferencePoint.uiConfig = headlineUiConfigHelper_1.getHeadlineUiConfig(newReferencePoint, this.intl);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        newReferencePoint = sort_1.removeSort(newReferencePoint);
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableHeadline.prototype.getExecution = function (options, insight, executionFactory) {
        var dateFormat = options.dateFormat, executionConfig = options.executionConfig;
        return executionFactory
            .forInsight(insight)
            .withDimensions(sdk_model_1.newDimension([sdk_model_1.MeasureGroupIdentifier]))
            .withDateFormat(dateFormat)
            .withExecConfig(executionConfig);
    };
    PluggableHeadline.prototype.checkBeforeRender = function (insight) {
        _super.prototype.checkBeforeRender.call(this, insight);
        var measureBucket = sdk_model_1.insightBucket(insight, sdk_ui_1.BucketNames.MEASURES);
        if (!measureBucket || sdk_model_1.bucketIsEmpty(measureBucket)) {
            // unmount on error because currently AD cannot recover in certain cases (RAIL-2625)
            this.unmount();
            throw new Visualization_1.InvalidBucketsSdkError();
        }
        return true;
    };
    PluggableHeadline.prototype.renderVisualization = function (options, insight, executionFactory) {
        if (!sdk_model_1.insightHasDataDefined(insight)) {
            return;
        }
        var locale = options.locale, _a = options.custom, custom = _a === void 0 ? {} : _a, config = options.config, customVisualizationConfig = options.customVisualizationConfig;
        var drillableItems = custom.drillableItems;
        var execution = this.getExecution(options, insight, executionFactory);
        this.renderFun(react_1.default.createElement(sdk_ui_charts_1.CoreHeadline, { execution: execution, drillableItems: drillableItems, onDrill: this.onDrill, locale: locale, config: sdk_ui_charts_1.updateConfigWithSettings(tslib_1.__assign(tslib_1.__assign({}, config), customVisualizationConfig), this.settings), afterRender: this.afterRender, onLoadingChanged: this.onLoadingChanged, pushData: this.pushData, onError: this.onError, LoadingComponent: null, ErrorComponent: null }), this.getElement());
    };
    PluggableHeadline.prototype.renderConfigurationPanel = function () {
        var _a;
        var configPanelElement = this.getConfigPanelElement();
        if (configPanelElement) {
            var properties = (_a = this.visualizationProperties) !== null && _a !== void 0 ? _a : {};
            this.renderFun(react_1.default.createElement(UnsupportedConfigurationPanel_1.default, { locale: this.locale, pushData: this.pushData, properties: propertiesHelper_1.getSupportedProperties(properties, this.supportedPropertiesList) }), configPanelElement);
        }
    };
    PluggableHeadline.prototype.mergeDerivedBucketItems = function (referencePoint, bucket, newDerivedBucketItems) {
        return bucket.items.reduce(function (resultItems, bucketItem) {
            var newDerivedBucketItem = bucketHelper_1.findDerivedBucketItem(bucketItem, newDerivedBucketItems);
            var shouldAddItem = newDerivedBucketItem &&
                !bucketHelper_1.isDerivedBucketItem(bucketItem) &&
                !bucketHelper_1.hasDerivedBucketItems(bucketItem, referencePoint.buckets);
            var shouldAddAfterMasterItem = bucket.localIdentifier === sdk_ui_1.BucketNames.MEASURES;
            if (shouldAddItem && !shouldAddAfterMasterItem) {
                resultItems.push(newDerivedBucketItem);
            }
            resultItems.push(bucketItem);
            if (shouldAddItem && shouldAddAfterMasterItem) {
                resultItems.push(newDerivedBucketItem);
            }
            return resultItems;
        }, []);
    };
    return PluggableHeadline;
}(AbstractPluggableVisualization_1.AbstractPluggableVisualization));
exports.PluggableHeadline = PluggableHeadline;
//# sourceMappingURL=PluggableHeadline.js.map